import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Typography, Space, Button, Select, Spin, Empty } from 'antd';
import { ReloadOutlined, SendOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import type { DeviceCommandItem } from './operationModels';
import { commandStatusColor, COMMAND_STATUS_OPTIONS, COMMAND_TYPE_OPTIONS } from './operationModels';

const { Text } = Typography;

interface DeviceCommandLogProps {
    accessToken?: string;
}

const DeviceCommandLog: React.FC<DeviceCommandLogProps> = ({ accessToken }) => {
    const [commands, setCommands] = useState<DeviceCommandItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

    const loadCommands = useCallback(async () => {
        setLoading(true);
        try {
            const params: Record<string, any> = {};
            if (statusFilter) params.status = statusFilter;
            const res = await axios.get(ENDPOINTS.DEVICE_COMMANDS, { withCredentials: true, params });
            const data = Array.isArray(res.data?.data)
                ? res.data.data
                : Array.isArray(res.data)
                    ? res.data
                    : [];
            setCommands(data);
        } catch (err) {
            console.error('Failed to load device commands:', err);
        } finally {
            setLoading(false);
        }
    }, [accessToken, statusFilter]);

    useEffect(() => {
        void loadCommands();
        const interval = setInterval(loadCommands, 10000);
        return () => clearInterval(interval);
    }, [loadCommands]);

    const columns: ColumnsType<DeviceCommandItem> = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 60,
            render: (id: number) => <Text code style={{ fontSize: 11 }}>#{id}</Text>,
        },
        {
            title: 'Device',
            dataIndex: 'device',
            width: 120,
            render: (_: any, rec: DeviceCommandItem) =>
                rec.device?.label || `Device #${rec.deviceId}`,
        },
        {
            title: 'Command',
            dataIndex: 'commandType',
            width: 130,
            render: (val: string) => {
                const found = COMMAND_TYPE_OPTIONS.find((o) => o.value === val);
                return <Tag color="blue">{found?.label || val}</Tag>;
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (val: string) => <Tag color={commandStatusColor(val)}>{val}</Tag>,
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            width: 70,
            render: (val: number) => (
                <Tag color={val >= 8 ? 'red' : val >= 5 ? 'orange' : 'default'}>
                    {val}
                </Tag>
            ),
        },
        {
            title: 'Mission',
            dataIndex: 'missionId',
            width: 90,
            render: (val: number | null, rec: DeviceCommandItem) =>
                val ? (rec.mission?.name || `#${val}`) : '—',
        },
        {
            title: 'Operation',
            dataIndex: 'operationId',
            width: 90,
            render: (val: number | null) => val ? `#${val}` : '—',
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            width: 140,
            render: (val: string) => val ? new Date(val).toLocaleString() : '—',
        },
        {
            title: 'Acknowledged',
            dataIndex: 'acknowledgedAt',
            width: 130,
            render: (val: string | null) =>
                val ? new Date(val).toLocaleTimeString() : '—',
        },
        {
            title: 'Completed',
            dataIndex: 'completedAt',
            width: 130,
            render: (val: string | null) =>
                val ? new Date(val).toLocaleTimeString() : '—',
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Select
                    allowClear
                    placeholder="Filter by status"
                    style={{ width: 180 }}
                    size="small"
                    value={statusFilter}
                    onChange={(val) => setStatusFilter(val)}
                    options={COMMAND_STATUS_OPTIONS.map((o) => ({
                        value: o.value,
                        label: (
                            <Space size={4}>
                                <Tag color={o.color} style={{ margin: 0 }}>{o.label}</Tag>
                            </Space>
                        ),
                    }))}
                />
                <Button icon={<ReloadOutlined />} size="small" onClick={loadCommands} loading={loading}>
                    Refresh
                </Button>
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 'auto' }}>
                    {commands.length} command{commands.length !== 1 ? 's' : ''} · auto-refreshes every 10s
                </Text>
            </div>
            <Table<DeviceCommandItem>
                rowKey="id"
                columns={columns}
                dataSource={commands}
                loading={loading}
                pagination={{ pageSize: 25, showSizeChanger: true, size: 'small' }}
                size="small"
                scroll={{ x: 1100 }}
                locale={{
                    emptyText: <Empty description="No device commands" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                }}
            />
        </div>
    );
};

export default DeviceCommandLog;
